import type { ReactNode } from 'react'
import cx from 'classnames'
import classicStyles from './ViewHeading.module.css'
import refreshStyles from './ViewHeading.refresh.module.css'
import { m, type MotionValue, useTransform } from 'motion/react'
import { Icon } from '@/components/Icon'
import { useAnimationProgress } from '@/contexts/animation-progress-context'
import { lerp } from '@/utils/math-util'
import { isRefreshDesign } from '@/infra/design'

const styles = isRefreshDesign ? refreshStyles : classicStyles

export type ViewHeadingSize = 'normal' | 'small'

export interface ViewHeadingProps {
  primaryText: ReactNode
  secondaryText: ReactNode
  visual?: ReactNode
  isDropdown?: boolean
  /**
   * A value from 0 (full) to 1 (compact) for controlling
   * the compaction animation. Defaults to 0. Can also be provided via the AnimationProgressContext..
   * The refreshed design's heading is a fixed breadcrumb row and ignores it.
   */
  compactionAnimationProgress?: MotionValue<number>
  viewHeadingSize?: ViewHeadingSize
}

function ClassicViewHeading({
  primaryText,
  secondaryText,
  visual,
  isDropdown,
  compactionAnimationProgress,
  viewHeadingSize = 'normal',
}: ViewHeadingProps) {
  const progress = useAnimationProgress(compactionAnimationProgress)

  const primaryStyle = {
    transform: useTransform(progress, (v) => `translateY(${lerp(0, 16, v)}px)`),
    opacity: useTransform(progress, (v) => lerp(1, 0, v)),
  }
  const secondaryStyle = {
    transform: useTransform(
      progress,
      // Approximately scales 24px text down to 16px.
      (v) => `translateY(${lerp(0, -2, v)}px) scale(${lerp(1, 0.8333334, v)})`,
    ),
  }
  const visualStyle = {
    transform: useTransform(
      progress,
      (v) =>
        `translate(${lerp(0, -8, v)}px, ${lerp(0, 8, v)}px) scale(${lerp(
          1,
          0.75,
          v,
        )})`,
    ),
  }
  const contentStyle = {
    // If we have a visual, it takes up space. But when we scale it down,
    // there is no reflow happening, which means the whitespace between the
    // text and the visual will be too wide. This offsets that.
    transform: useTransform(progress, (v) =>
      visual ? `translateX(${lerp(0, -16, v)}px)` : '',
    ),
  }
  return (
    <div className={cx(styles.root, isDropdown && styles.clickable)}>
      {visual && (
        <m.div className={styles.visual} style={visualStyle}>
          {visual}
        </m.div>
      )}
      <m.div className={styles.content} style={contentStyle}>
        <m.p className={styles.primary} style={primaryStyle}>
          {primaryText}
        </m.p>
        <m.p
          className={cx(styles.secondary, styles[viewHeadingSize])}
          style={secondaryStyle}
        >
          <span className={styles.secondaryText}>{secondaryText}</span>
          {isDropdown && (
            <span className={styles.icon}>
              <Icon
                name="arrow"
                rotate={90}
                size={viewHeadingSize === 'small' ? 16 : 24}
              />
            </span>
          )}
        </m.p>
      </m.div>
    </div>
  )
}

/**
 * The refreshed design's heading: one row reading `Primary / Secondary`, sized
 * for its fixed-height page header.
 */
function BreadcrumbViewHeading({
  primaryText,
  secondaryText,
  visual,
  isDropdown,
  viewHeadingSize = 'normal',
}: ViewHeadingProps) {
  return (
    <div className={cx(styles.root, isDropdown && styles.clickable)}>
      {visual && <div className={styles.visual}>{visual}</div>}
      <div className={styles.content}>
        <p className={styles.primary}>{primaryText}</p>
        <p className={cx(styles.secondary, styles[viewHeadingSize])}>
          <span className={styles.secondaryText}>{secondaryText}</span>
          {isDropdown && (
            <span className={styles.icon}>
              <Icon name="arrow" rotate={90} size={16} />
            </span>
          )}
        </p>
      </div>
    </div>
  )
}

/**
 * The heading of a view.
 */
export const ViewHeading = isRefreshDesign
  ? BreadcrumbViewHeading
  : ClassicViewHeading
